/** URMAREA UNEI SCRIERI: ce a rămas în catalog după ce cineva a apăsat un buton.
 *
 *  DE CE EXISTĂ. Editorul are două butoane care scriu, și ele scriu două lucruri
 *  diferite: «Salvează ciorna» lasă un rând pe care motoarele nu-l citesc,
 *  «Publică» îl face versiunea pe care o iau planurile. Amândouă se întorceau
 *  însă fără nicio vorbă despre ce s-a întâmplat, iar singurul semn era chipul
 *  de lângă titlu.
 *
 *  Raportat de Stefan, 17.09.2026: «când dau salvează ca ciornă trebuie salvat ca
 *  draft, dar când dau publică trebuie salvat ca variantă finală; acum îmi trimite
 *  niște notificări confusing în bară și rămâne salvat ca ciornă și după publish».
 *
 *  CE E AICI ȘI CE NU. Aici stau cele două urmări cu putință și fraza fiecăreia;
 *  scrierea propriu-zisă rămâne în `pages/Editor.tsx`. Despărțite așa, fraza se
 *  poate proba fără ecran.
 *
 *  NU MAI E NICIO PROBĂ ÎN EDITOR (Stefan, 19.09.2026): «scoate toate validările
 *  din editorul de tronsoane, nu sunt up to date și mai mult încurcă». Deci
 *  nicio scriere nu mai poate fi REFUZATĂ pe desen, iar frazele de aici nu mai
 *  numără nimic — spun doar ce s-a scris și ce înseamnă.
 */
import { t } from "../../../i18n";

/** Cele două urmări cu putință ale celor două butoane care scriu.
 *
 *  `ciorna` — s-a scris un rând `draft`: nimic din el nu ajunge în vreun plan.
 *  `publicat` — rândul e `published`: de-acum planurile proiectului îl folosesc.
 */
export type Urmarea = "ciorna" | "publicat";

export type Scriere = {
  urmarea: Urmarea;
  /** Versiunea rândului scris. */
  versiune?: number;
};

/** Ce se scrie în capul barei de jos: ce s-a întâmplat și ce înseamnă. */
export function antetul(s: Scriere): { titlu: string; sfat: string } {
  const v = { versiune: s.versiune ?? "?", publica: t("editor.publica") };
  if (s.urmarea === "publicat") {
    return {
      titlu: t("editor.urmarea_publicat", v),
      sfat: t("editor.urmarea_publicat_sfat", v),
    };
  }
  return {
    titlu: t("editor.urmarea_ciorna", v),
    sfat: t("editor.urmarea_ciorna_sfat", v),
  };
}

/** «Publică» trebuie să scrie o ciornă ÎNAINTE?
 *
 *  Publicarea nu trimite documentul: trece pe `published` un rând care există
 *  deja, al PROIECTULUI. Deci are nevoie de o scriere înainte în două cazuri, nu
 *  într-unul:
 *
 *   * e ceva nescris pe ecran — altfel s-ar publica desenul de dinainte;
 *   * modulul e încă cel GLOBAL, neatins de proiectul ăsta — atunci proiectul
 *     n-are nicio versiune a lui, iar serverul răspunde, pe drept, «salvează una
 *     întâi». Cerută de o singură apăsare: cine deschide un modul global și
 *     apasă «Publică» ca să-l facă al proiectului nu are de unde să ghicească
 *     drumul prin «Salvează ciorna».
 */
export function cereCiornaIntai(
  o: { nesalvat: boolean; alProiectului: boolean },
): boolean {
  return o.nesalvat || !o.alProiectului;
}

/** Does this module already have a row that publishing can flip? Always in a
 *  person's collection; on a project, only once the project has rewritten it. */
export function areRandul(o: { alMeu: boolean; scope?: string }): boolean {
  return o.alMeu || o.scope === "proiect";
}

/** The version a save started from. A global module's numbers are not this
 *  module's history, so it has none. */
export function versiuneaDeBaza(
  meta: { version: number; scope: string } | null | undefined,
): number | undefined {
  return !meta || meta.scope === "global" ? undefined : meta.version;
}

/** What «Publică» sends: the draft it just wrote, else the version open. */
export function versiuneaDePublicat(
  o: { salvata?: number; deschisa?: number },
): number | undefined {
  return o.salvata ?? o.deschisa;
}
